import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Briefcase, ChevronLeft, MapPin, DollarSign, Sparkles } from 'lucide-react';

const FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'offer', label: 'Offer received' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'withdrawn', label: 'Withdrawn' },
];

const STATUS_BADGE = {
    applied: { label: 'Applied', cls: 'text-blue-700 border-blue-300 bg-blue-50' },
    reviewed: { label: 'Reviewed', cls: 'text-amber-700 border-amber-300 bg-amber-50' },
    shortlisted: { label: 'Shortlisted', cls: 'text-purple-700 border-purple-300 bg-purple-50' },
    offer_sent: { label: 'Offer received', cls: 'text-green-700 border-green-300 bg-green-50' },
    rejected: { label: 'Not selected', cls: 'text-red-600 border-red-300 bg-red-50' },
    withdrawn: { label: 'Withdrawn', cls: 'text-zinc-500 border-zinc-300 bg-zinc-50' },
};

function companyInitial(name) {
    if (!name) return '?';
    return name.trim().charAt(0).toUpperCase();
}

function matchFilter(app, tab) {
    if (tab === 'all') return true;
    if (tab === 'active') return ['applied', 'reviewed', 'shortlisted'].includes(app.status);
    if (tab === 'offer') return app.status === 'offer_sent';
    if (tab === 'rejected') return app.status === 'rejected';
    if (tab === 'withdrawn') return app.status === 'withdrawn';
    return true;
}

function ApplicationDetail({ application, onBack, onNavigateToOffers }) {
    const app = application;
    const badge = STATUS_BADGE[app.status] || { label: app.status, cls: 'text-zinc-600 border-zinc-300' };
    return (
        <div data-testid={`my-application-detail-${app.id}`}>
            <Button variant="ghost" onClick={onBack} className="rounded-none h-9 px-3 text-xs uppercase tracking-[0.15em] font-bold mb-4" data-testid="my-application-back-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to My Applications
            </Button>

            <div className="border border-zinc-200 p-5 mb-4">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-14 h-14 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0 font-heading text-xl font-bold">
                        {companyInitial(app.employer_company_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="font-heading text-xl font-bold" data-testid="my-application-title">{app.job_title || 'Job'}</h2>
                            <Badge variant="outline" className={`rounded-none text-[10px] ${badge.cls}`} data-testid={`my-application-status-${app.id}`}>
                                {badge.label}
                            </Badge>
                        </div>
                        <p className="text-sm text-zinc-500 mt-1">{app.employer_company_name || 'Company'}</p>
                    </div>
                </div>
                <Separator className="mb-4" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">Location</p>
                        <p className="text-sm font-semibold mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {app.job_location || '—'}, {app.job_country || '—'}</p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">Applied on</p>
                        <p className="text-sm font-semibold mt-1">{app.applied_at ? new Date(app.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</p>
                    </div>
                </div>
                {app.withdrawal_reason === 'offer_accepted' && (
                    <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600">
                        This application was automatically withdrawn because you accepted an offer.
                    </div>
                )}
            </div>

            {app.status === 'offer_sent' && (
                <Button
                    onClick={onNavigateToOffers}
                    className="w-full h-14 rounded-none bg-zinc-900 text-white font-semibold"
                    data-testid={`my-application-view-offer-${app.id}`}
                >
                    View offer
                </Button>
            )}
        </div>
    );
}

export default function WorkerMyApplications({ refreshToken, onNavigateToOffers }) {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState('all');
    const [openedId, setOpenedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await api.get('/worker/applications');
            setApps(data.applications || []);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || 'Failed to load your applications.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load, refreshToken]);

    const filtered = apps.filter(a => matchFilter(a, tab));

    if (openedId) {
        const app = apps.find(a => a.id === openedId);
        if (app) {
            return (
                <ApplicationDetail
                    application={app}
                    onBack={() => setOpenedId(null)}
                    onNavigateToOffers={onNavigateToOffers}
                />
            );
        }
    }

    return (
        <div data-testid="worker-my-applications">
            <div className="mb-4">
                <h2 className="font-heading text-2xl font-bold mb-1">My Applications</h2>
                <p className="text-sm text-zinc-500">Track the jobs you've applied to and the employer's response.</p>
            </div>

            <div className="flex gap-2 flex-wrap mb-4" data-testid="my-applications-filters">
                {FILTER_TABS.map(t => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={`h-9 px-3 text-xs font-semibold border transition-colors ${tab === t.key ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 hover:border-zinc-400 text-zinc-700'}`}
                        data-testid={`my-applications-filter-${t.key}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {error && (
                <div className="border border-red-200 bg-red-50 p-3 mb-4 text-sm text-red-700">{error}</div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-12 border border-zinc-200" data-testid="my-applications-empty">
                    <Briefcase className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                    <p className="font-heading font-bold text-base mb-1">
                        {apps.length === 0 ? 'No applications yet' : 'No applications in this view'}
                    </p>
                    <p className="text-sm text-zinc-500 max-w-md mx-auto">
                        {apps.length === 0
                            ? 'Browse job openings and apply to positions that fit your experience.'
                            : 'Switch to another tab to see your other applications.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="my-applications-list">
                    {filtered.map(app => {
                        const badge = STATUS_BADGE[app.status] || { label: app.status, cls: 'text-zinc-600 border-zinc-300' };
                        return (
                            <button
                                key={app.id}
                                type="button"
                                onClick={() => setOpenedId(app.id)}
                                className="w-full text-left border border-zinc-200 p-4 hover:border-zinc-900 transition-colors bg-white"
                                data-testid={`my-application-${app.id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-12 h-12 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0 font-heading text-lg font-bold">
                                        {companyInitial(app.employer_company_name)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-semibold text-base truncate">{app.job_title || 'Job'}</h3>
                                            <Badge variant="outline" className={`rounded-none text-[10px] ${badge.cls}`}>
                                                {badge.label}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-zinc-500 mt-1 truncate">{app.employer_company_name || 'Company'}</p>
                                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-600">
                                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {app.job_location || '—'}, {app.job_country || '—'}</span>
                                            <span>Applied {app.applied_at ? new Date(app.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</span>
                                        </div>
                                        {app.status === 'offer_sent' && (
                                            <p className="text-[11px] text-green-700 mt-2 flex items-center gap-1">
                                                <Sparkles className="w-3 h-3" /> You've received an offer! Tap to view.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
